import type { ReactNode } from "react";
import Sparkline from "./Sparkline";
import { CHART_TONE } from "./chartKit";
import { TONE_TEXT, type Tone } from "./StatusDot";
import { delta as fmtDelta } from "../lib/format";

/**
 * Resolved colour for a tone — SVG strokes cannot read CSS vars reliably, so a
 * sparkline needs the value, not the `var()`. Derived from `chartKit` so the
 * palette has exactly one source.
 *
 * A function, not a `Record` built at module scope: `CHART_TONE`'s members are
 * getters that read `getComputedStyle(<html>)`, so a table built at import time
 * would freeze whatever those getters returned *before the first render* — and
 * before the stylesheet is in the document that is the empty string. It only
 * ever worked because `chartKit` imports `index.css`. Reading per call moves
 * the read to render time, where the tokens are guaranteed to exist.
 */
export function toneHex(tone: Tone): string {
  switch (tone) {
    case "muted":
      return CHART_TONE.muted;
    case "accent":
      return CHART_TONE.accent;
    case "money":
      return CHART_TONE.money;
    case "business":
      return CHART_TONE.business;
    case "ok":
      return CHART_TONE.ok;
    case "warn":
      return CHART_TONE.warn;
    case "danger":
      return CHART_TONE.danger;
    case "info":
      return CHART_TONE.info;
    default:
      return CHART_TONE.fg;
  }
}

interface StatCardProps {
  label: string;
  /** Pre-formatted through lib/format — the card never formats numbers itself. */
  value: ReactNode;
  unit?: string;
  hint?: ReactNode;
  /** Percent change vs the previous comparable period. */
  delta?: number | null;
  deltaLabel?: string;
  /** Which direction is good. "down" flips the colours (spending, for example). */
  deltaGoodWhen?: "up" | "down" | "none";
  tone?: Tone;
  size?: "sm" | "md" | "lg";
  spark?: number[];
  footer?: ReactNode;
  badge?: ReactNode;
  className?: string;
}

/**
 * The workhorse. Numbers are the interface here: big, tabular, with the unit
 * and the delta-vs-previous glued to them.
 */
export default function StatCard({
  label,
  value,
  unit,
  hint,
  delta = undefined,
  deltaLabel,
  deltaGoodWhen = "up",
  tone = "default",
  size = "md",
  spark,
  footer,
  badge,
  className = "",
}: StatCardProps) {
  // Phones get one step down the scale: a 28px number next to a 187px card is
  // the difference between two KPIs per row and one.
  const valueSize =
    size === "lg"
      ? "text-metric sm:text-metric-lg"
      : size === "sm"
        ? "text-metric-sm"
        : "text-metric-sm sm:text-metric";

  let deltaTone: Tone = "muted";
  if (delta !== undefined && delta !== null && delta !== 0 && deltaGoodWhen !== "none") {
    const good = deltaGoodWhen === "up" ? delta > 0 : delta < 0;
    deltaTone = good ? "ok" : "danger";
  }

  return (
    <div className={`jv-card flex min-w-0 flex-col gap-1.5 p-3 sm:gap-2 sm:p-4 ${className}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 text-micro font-medium text-muted">{label}</span>
        {badge}
      </div>

      <div className="flex min-w-0 items-baseline gap-2">
        <span className={`jv-num min-w-0 truncate font-medium ${valueSize} ${TONE_TEXT[tone]}`}>
          {value}
        </span>
        {unit && <span className="text-sm text-muted">{unit}</span>}
      </div>

      {(delta !== undefined || hint) && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          {delta !== undefined && (
            <span className={`jv-num text-micro font-medium ${TONE_TEXT[deltaTone]}`}>
              {fmtDelta(delta ?? null)}
            </span>
          )}
          {deltaLabel && delta !== undefined && delta !== null && (
            <span className="text-muted">{deltaLabel}</span>
          )}
          {hint && <span className="text-muted">{hint}</span>}
        </div>
      )}

      {spark && spark.length > 1 && (
        <Sparkline values={spark} color={toneHex(tone)} height={30} className="mt-1" />
      )}

      {footer && <div className="mt-auto pt-1 text-xs text-muted">{footer}</div>}
    </div>
  );
}

/**
 * The KPI row every page opens with. One definition so the four pages (and the
 * loading skeleton that stands in for them) cannot drift apart.
 *
 * Two columns even on the narrowest phone: four full-width KPI cards is four
 * screenfuls of scrolling to read four numbers.
 *
 * Four columns from `lg`, not `md` — corrected 2026-08-15 after measuring. At
 * 768px the sidebar is already open, so four cards leave 92px of value width
 * and «18 276 ₴» needs 94: the `truncate` in `StatCard` cut it to «18 276…»,
 * which does not read as a clipped number but as a smaller one. On /finance at
 * 900px, nine of twelve money figures were clipped. 768 is iPad portrait, so
 * this was a real device sitting in the failing band, and a wrong magnitude is
 * worse than a 2x2 block. At `lg` four columns have 168px against the 132px a
 * hryvnia figure needs at full size.
 */
export function StatGrid({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4 ${className}`}>
      {children}
    </div>
  );
}
